import React from 'react';
import { Stack, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { EmptyState, ListTable, Modal, Panel, SearchField, StatusChip, type ListColumn } from '../../../ui-kit';
import { fetchCwRuns, type CwRunCard } from '../../../services/carrierWebsiteService';
import { fromNow, matches, useLive, when } from '../rocco/live';
import { useAgentAddress } from './address';
import RunPage from './RunPage';
import { money, runChip } from './status';

/**
 * Every run, newest first. Updates by itself. A run opens in a window over the
 * list; its address (/runs/<id>) opens the same window, so links still work.
 */
export default function RunsPage() {
  const { data, error, loading } = useLive(fetchCwRuns, 3000);
  const navigate = useNavigate();
  const base = useAgentAddress();
  const { itemId } = useParams();
  const opened = (data?.runs ?? []).find((r) => r.id === itemId);
  const [query, setQuery] = React.useState('');
  const all = data?.runs ?? [];
  const rows = all.filter((r) => matches(query, [r.customer, r.carrier_name, r.stage, r.by]));

  const columns: ListColumn<CwRunCard>[] = [
    {
      key: 'customer', header: 'Customer', grow: 1, minWidth: 180, sortValue: (r) => r.customer,
      render: (r) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{r.customer || 'Customer'}</Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{r.carrier_name}</Typography>
        </Stack>
      ),
    },
    {
      key: 'stage', header: 'Where it is', grow: 0.8, minWidth: 200, sortValue: (r) => r.stage,
      render: (r) => <StatusChip {...runChip(r)} />,
    },
    {
      key: 'price', header: 'Price', width: 150, sortValue: (r) => r.price,
      render: (r) => (
        <Stack>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontVariantNumeric: 'tabular-nums' })}>{money(r.price, r.currency)}</Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
            {r.price_kind === 'firm' ? 'Firm price' : r.price_kind === 'indication' ? 'Rough price' : ''}
          </Typography>
        </Stack>
      ),
    },
    {
      key: 'how', header: 'How', width: 150, sortValue: (r) => r.mode,
      render: (r) => (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>
          {r.mode === 'learn' ? 'First visit' : 'Followed the guide'}
        </Typography>
      ),
    },
    {
      key: 'started', header: 'Started', width: 130, sortValue: (r) => r.created_at, sortStartsDescending: true,
      render: (r) => (
        <Stack>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontVariantNumeric: 'tabular-nums' })}>{when(r.created_at)}</Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{[fromNow(r.created_at), r.by].filter(Boolean).join(' · ')}</Typography>
        </Stack>
      ),
    },
  ];

  if (error && !data) return <EmptyState message={error} />;
  return (
    <>
      <Panel title="Runs" subtitle={`${all.length} run${all.length === 1 ? '' : 's'} · newest first · updates by itself`}
        search={<SearchField value={query} onChange={setQuery} placeholder="Search runs…" />}>
        <ListTable columns={columns} rows={rows} getRowKey={(r) => r.id} pageSize={25} loading={loading}
          defaultSort={{ key: 'started', direction: 'desc' }} onRowClick={(r) => navigate(`${base}/runs/${r.id}`)}
          emptyMessage={all.length ? 'Nothing matches. Clear the search.' : 'No runs yet. Open New run to start one.'} />
      </Panel>
      {itemId ? (
        <Modal open onClose={() => navigate(`${base}/runs`)} size="lg"
          title={opened?.customer ?? 'Run'} subtitle={opened ? opened.carrier_name : undefined}>
          <RunPage runId={itemId} embedded />
        </Modal>
      ) : null}
    </>
  );
}